import { colors } from '../constants/colors';

export function TermsOfServicePage() {
  return (
    <div style={{ flex: 1, padding: '40px 24px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', color: colors.ink.black, marginBottom: '8px' }}>Terms of Service</h1>
      <p style={{ color: colors.gray[600], fontSize: '14px', marginBottom: '32px' }}>Last updated: April 9, 2026</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', color: colors.gray[900], fontSize: '15px', lineHeight: '1.7' }}>
        <section>
          <h2 style={sectionTitle}>1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Snagg platform, including the website at snagg.live and the Snagg mobile application
            (collectively, the "Service"), operated by Snagg, LLC ("Snagg," "we," "us," or "our"), you agree to be bound by
            these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>2. Eligibility</h2>
          <p>
            You must be at least 18 years old to use the Service. By using the Service, you represent and warrant that you
            are at least 18 years of age, have the legal capacity to enter into these Terms, and are not prohibited from
            using the Service under applicable law.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>3. Accounts</h2>
          <ul style={listStyle}>
            <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</li>
            <li>You agree to provide accurate, current, and complete information during registration and to keep your account information up to date.</li>
            <li>You may not create more than one account, share your account with others, or transfer your account to another person.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these Terms or that we reasonably believe are being used fraudulently.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>4. The Snagg Marketplace</h2>
          <p>
            Snagg is a marketplace platform that enables sellers to list and sell items through live auction shows and
            buy-it-now listings. Snagg is not a party to the transactions between buyers and sellers. We provide the
            platform and tools; the sale is between the buyer and the seller.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>5. Buying on Snagg</h2>

          <h3 style={subTitle}>Auctions</h3>
          <p>
            When you place a bid in a live auction, you are making a binding offer to purchase the item at that price.
            If you are the winning bidder, you are obligated to complete the purchase. Bids cannot be retracted once placed.
          </p>

          <h3 style={subTitle}>Buy-It-Now</h3>
          <p>
            When you purchase an item through buy-it-now, you are entering into a binding agreement to purchase the item
            at the listed price.
          </p>

          <h3 style={subTitle}>Payment</h3>
          <ul style={listStyle}>
            <li>All payments are processed securely through Stripe. By making a purchase, you authorize us to charge your payment method for the total amount, including the item price, applicable taxes, and shipping fees.</li>
            <li>You agree to pay all amounts due promptly. Failure to pay may result in order cancellation and account suspension.</li>
          </ul>

          <h3 style={subTitle}>Shipping</h3>
          <p>
            Sellers are responsible for shipping items to buyers within the timeframe specified on the platform. Shipping
            costs are displayed before purchase. Snagg is not responsible for items lost or damaged during shipping, though
            we will assist in resolving disputes.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>6. Selling on Snagg</h2>
          <ul style={listStyle}>
            <li><strong>Eligibility:</strong> to sell on Snagg, you must complete seller verification and connect a valid payout account through Stripe Connect.</li>
            <li><strong>Listings:</strong> you are responsible for the accuracy of your item descriptions, photos, and condition grades. Misrepresenting items is a violation of these Terms.</li>
            <li><strong>Authenticity & accurate representation:</strong> all items listed on Snagg must be authentic and accurately described. Sellers are strictly prohibited from listing counterfeit, replica, knockoff, or otherwise inauthentic items, or from misrepresenting the brand, origin, condition, materials, provenance, or any other material characteristic of an item. This includes, but is not limited to, listing items with misleading photos, descriptions, or condition grades, or omitting known defects or damage. Sellers must disclose all material facts about an item that a reasonable buyer would consider relevant to their purchase decision. Snagg reserves the right to require sellers to provide proof of authenticity at any time, and failure to provide satisfactory proof may result in listing removal, sale reversal, and account action up to and including permanent removal from the platform.</li>
            <li><strong>Live shows:</strong> sellers may host live auction shows. You are responsible for all content broadcast during your shows and must comply with our content guidelines.</li>
            <li><strong>Fulfillment:</strong> you must ship sold items within the timeframe required by the platform. Failure to fulfill orders may result in penalties, suspension, or removal from the platform.</li>
            <li><strong>Fees and payouts:</strong> Snagg charges a platform fee on each sale. Fee rates are disclosed before you list items. Payouts are processed according to our payout schedule and are subject to holds for fraud prevention.</li>
            <li><strong>Taxes:</strong> you are responsible for collecting and remitting any applicable sales taxes. Snagg may collect taxes on your behalf where required by law.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>7. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul style={listStyle}>
            <li>Violate any applicable law, regulation, or third-party rights.</li>
            <li>List or sell counterfeit, replica, knockoff, stolen, illegal, or prohibited items, or misrepresent items in any way, including their authenticity, brand, origin, condition, or material characteristics.</li>
            <li>Manipulate auctions through shill bidding, bid shielding, or other deceptive practices.</li>
            <li>Harass, threaten, or abuse other users, including during live shows.</li>
            <li>Post, stream, or transmit content that is violent, graphic, or promotes violence against any individual or group.</li>
            <li>Use discriminatory, hateful, or derogatory language based on race, ethnicity, gender, sexual orientation, religion, disability, or any other protected characteristic.</li>
            <li>Post, stream, or transmit sexually explicit content, nudity, or sexual expletives.</li>
            <li>Use the Service to distribute spam, malware, or other harmful content.</li>
            <li>Circumvent platform fees by arranging off-platform transactions with users you met on Snagg.</li>
            <li>Scrape, crawl, or use automated tools to access the Service without our written permission.</li>
            <li>Attempt to gain unauthorized access to the Service, other accounts, or our systems.</li>
            <li>Interfere with or disrupt the Service or its infrastructure.</li>
          </ul>
        </section>

        <section>
          <h2 style={sectionTitle}>8. Content</h2>
          <p>
            You retain ownership of content you upload to the Service (photos, descriptions, live video, messages). By
            uploading content, you grant Snagg a non-exclusive, worldwide, royalty-free license to use, display, reproduce,
            and distribute that content in connection with operating and promoting the Service.
          </p>
          <p style={{ marginTop: '8px' }}>
            You are solely responsible for content you upload or broadcast. Content must not be illegal, obscene, defamatory,
            infringing, or otherwise objectionable. We reserve the right to remove content that violates these Terms.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>9. Disputes Between Users</h2>
          <p>
            Snagg may assist in resolving disputes between buyers and sellers but is not obligated to do so. We may, at our
            discretion, issue refunds, reverse transactions, or take other action we deem appropriate. Our decisions in
            dispute resolution are final.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>10. Intellectual Property</h2>
          <p>
            The Service, including its design, features, code, and branding, is owned by Snagg, LLC and is protected by
            copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create
            derivative works based on the Service without our written permission.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>11. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
            BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO
            NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>
          <p style={{ marginTop: '8px' }}>
            SNAGG DOES NOT ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR THE QUALITY, SAFETY, LEGALITY, OR AUTHENTICITY
            OF ITEMS LISTED ON THE PLATFORM. ALL TRANSACTIONS ARE BETWEEN BUYERS AND SELLERS.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>12. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, SNAGG, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
            REVENUE, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING
            FROM THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO SNAGG IN THE 12 MONTHS PRECEDING
            THE CLAIM.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>13. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Snagg and its officers, directors, employees, and agents from
            any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from your
            use of the Service, your violation of these Terms, or your violation of any rights of another party.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>14. Enforcement & Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time, with or without cause, and with or without
            notice. Upon termination, your right to use the Service ceases immediately. Sections that by their nature should
            survive termination (including disclaimers, limitation of liability, and indemnification) will survive.
          </p>
          <p style={{ marginTop: '8px' }}>
            Without limiting the foregoing, Snagg specifically reserves the right to immediately suspend or permanently
            remove any seller from the platform who is found to have listed, sold, or attempted to sell counterfeit,
            inauthentic, or materially misrepresented items. In such cases, Snagg may, at its sole discretion, take any
            or all of the following actions: remove the seller's listings, cancel pending orders, reverse completed sales,
            withhold or claw back payouts, issue refunds to affected buyers, and permanently ban the seller from the
            platform. Snagg may also report suspected counterfeiting or fraud to the appropriate legal authorities. These
            remedies are in addition to any other rights or remedies available to Snagg under these Terms or applicable law.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>15. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws provisions.
            Any disputes arising under these Terms shall be resolved in the state or federal courts located in Delaware, and
            you consent to the jurisdiction of those courts.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>16. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms
            on this page and updating the "Last updated" date. Your continued use of the Service after changes are posted
            constitutes your acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2 style={sectionTitle}>17. Contact Us</h2>
          <p>If you have questions about these Terms, please contact us at:</p>
          <p style={{ marginTop: '8px' }}>
            Snagg, LLC<br />
            Email: support@snagg.live
          </p>
        </section>
      </div>
    </div>
  );
}

const sectionTitle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#000000',
  marginBottom: '8px',
};

const subTitle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#000000',
  marginTop: '12px',
  marginBottom: '4px',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};
